import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Flag, Users } from 'lucide-react';
import {
  ChallengeBoard, ChallengeBoardKind, ChallengeBoardRow, ChallengeMe, ChallengeRide, ChallengeRideDraft, ChallengeSummary, challengeEditable, TEAM_REQUEST_TEXTS,
} from '@radevents/shared';
import { ChallengeHero, ChallengeRules, JoinDialog, JoinDraft, Leaderboard, MyChallenge, NameForm, RideForm } from './ChallengeParts';

/**
 * Kilometer, die am selben Tag schon in der eigenen Liste stehen, ohne die gerade bearbeitete
 * Fahrt: die Tagesgrenze (400 km) gilt für die Summe, und beim Bearbeiten zählt der alte Wert der
 * Fahrt nicht doppelt.
 */
export function kmSameDayOf(rides: ChallengeRide[], rideDate: string, excludeId: number | null = null): number {
  const sum = rides.filter(r => r.rideDate === rideDate && r.id !== excludeId).reduce((s, r) => s + r.km, 0);
  return Math.round(sum * 10) / 10;
}

/**
 * Die Seite `/winterchallenge` ohne Abrufe: Daten und Handlungen kommen von außen, damit das
 * Design-Lab sie mit Fixtures zeigt und die Seite selbst nur lädt und speichert
 * (`pages/Winterchallenge.tsx`).
 *
 * Mitmachen öffnet einen Dialog, Eintragen ein Formular im persönlichen Bereich.
 */
export default function WinterchallengeView({
  summary, today, me, boards, boardsUnavailable = {}, loggedIn, joinInitial, joinTeamName = null, joinTeamRequest = false, initialPanel = null, saving = false, error = null,
  onClearError, onJoin, onLogin, onSaveRide, onDeleteRide, onRename, onReport, onShowAll, onCreateTeam,
}: {
  summary: ChallengeSummary;
  today: string;
  /** `null` = angemeldet, aber nicht dabei, oder nicht angemeldet. */
  me: ChallengeMe | null;
  boards: Partial<Record<ChallengeBoardKind, ChallengeBoard>>;
  /** Wertungen, deren Abruf gescheitert ist: dort steht nicht „noch niemand“, sondern „nicht erreichbar“. */
  boardsUnavailable?: Partial<Record<ChallengeBoardKind, boolean>>;
  loggedIn: boolean;
  /** Vorbelegung für „Mitmachen“ aus dem Konto. */
  joinInitial?: Partial<JoinDraft>;
  /** Über einen Einladungslink gekommen: „Mitmachen“ nennt das Team, dem man danach gleich angehört. */
  joinTeamName?: string | null;
  /** Von der Teamseite ohne Link: nach dem Mitmachen wird angefragt. */
  joinTeamRequest?: boolean;
  /** Mitmachen, Eintragen bzw. Umbenennen schon geöffnet (Rückkehr von der Anmeldung, Design-Lab). */
  initialPanel?: 'join' | 'ride' | 'name' | null;
  saving?: boolean;
  /** Meldung des Servers zum offenen Formular. */
  error?: string | null;
  /** Formular geschlossen oder gewechselt: die alte Meldung gehört nicht zum nächsten. */
  onClearError?: () => void;
  onJoin?: (draft: JoinDraft) => Promise<void> | void;
  /** Nicht angemeldet: der Code-Ablauf, danach weiter mit Mitmachen. */
  onLogin?: () => void;
  /** `rideId` gesetzt = bearbeiten. */
  onSaveRide?: (draft: ChallengeRideDraft, rideId: number | null) => Promise<void> | void;
  onDeleteRide?: (ride: ChallengeRide) => void;
  onRename?: (displayName: string) => Promise<void> | void;
  onReport?: (row: ChallengeBoardRow) => void;
  onShowAll?: (kind: ChallengeBoardKind) => void;
  onCreateTeam?: () => void;
}) {
  const [panel, setPanel] = useState<'join' | 'ride' | 'name' | null>(initialPanel);
  const [editing, setEditing] = useState<ChallengeRide | null>(null);
  /** Das Datum im Formular, für die Tagessumme; bis zur ersten Änderung das der Fahrt bzw. heute. */
  const [rideDate, setRideDate] = useState<string | null>(null);
  const startJoin = () => { onClearError?.(); return loggedIn ? setPanel('join') : onLogin?.(); };
  const openRide = (ride: ChallengeRide | null) => { onClearError?.(); setEditing(ride); setRideDate(null); setPanel('ride'); };
  const close = () => { onClearError?.(); setPanel(null); setEditing(null); setRideDate(null); };

  // Mit offenem Formular angekommen (`?eintragen=1`): dorthin scrollen, sonst steht es unter dem Kopf.
  const meArea = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialPanel !== 'ride') return;
    const form = meArea.current?.querySelector('.wc-form');
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    form?.scrollIntoView?.({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
  }, [initialPanel]);

  const formDate = rideDate ?? editing?.rideDate ?? (today > summary.endsOn ? summary.endsOn : today);
  const kmSameDay = me ? kmSameDayOf(me.rides, formDate, editing?.id ?? null) : 0;

  return (
    <div className="wc-page">
      {/* Mitmachen steht einmal, im Kopf. Eine zweite Karte darunter wiederholte nur den Knopf. */}
      <ChallengeHero summary={summary} today={today} joined={!!me} loggedIn={loggedIn}
        onJoin={startJoin} onAddRide={() => openRide(null)} />

      {/* Gäste haben keinen eigenen Bereich. Ein leerer stünde trotzdem als Zeile im Raster und
          schöbe die Spielregeln um den Zeilenabstand unter die Rangliste. */}
      <div className={`wc-columns${me ? '' : ' wc-columns--guest'}`}>
        {me && (
          <div ref={meArea} className="wc-area-me" style={{ display: 'grid', gap: 22 }}>
            <MyChallenge me={me} summary={summary} today={today}
              formOpen={panel === 'ride'} onAddRide={() => openRide(null)}
              onEditRide={r => openRide(r)} onDeleteRide={onDeleteRide}
              onEditName={onRename ? () => { onClearError?.(); setEditing(null); setPanel('name'); } : undefined}
              nameForm={panel === 'name' ? (
                <NameForm initial={me.displayName} saving={saving} serverError={error}
                  onSubmit={async name => {
                    try { await onRename?.(name); close(); } catch { /* die Meldung steht im Formular */ }
                  }}
                  onCancel={close} />
              ) : undefined}
              form={
                <RideForm key={editing?.id ?? 'neu'} summary={summary} today={today} saving={saving} serverError={error}
                  kmSameDay={kmSameDay} onDateChange={setRideDate}
                  initial={editing ? { rideDate: editing.rideDate, km: String(editing.km).replace('.', ','), bikeType: editing.bikeType, proofUrl: editing.proofUrl ?? '' } : undefined}
                  onSubmit={async d => {
                    try { await onSaveRide?.(d, editing?.id ?? null); close(); } catch { /* die Meldung steht im Formular */ }
                  }}
                  onCancel={close} />
              } />
            <TeamCard me={me} onCreateTeam={today < summary.startsOn || challengeEditable(summary, today) ? onCreateTeam : undefined} />
          </div>
        )}
        <div className="wc-area-board">
          <Leaderboard boards={boards} unavailable={boardsUnavailable} summary={summary} onShowAll={onShowAll} onReport={loggedIn && me ? onReport : undefined} />
        </div>
        <div className="wc-area-rules"><ChallengeRules summary={summary} /></div>
      </div>
      {panel === 'join' && !me && (
        <JoinDialog initial={joinInitial} teamName={joinTeamName} teamRequest={joinTeamRequest} serverError={error} saving={saving}
          onSubmit={async d => {
            try { await onJoin?.(d); setPanel(null); } catch { /* die Meldung steht im Dialog */ }
          }}
          onClose={close} />
      )}
    </div>
  );
}

/** Mein Team oder der Weg zu einem: Einladungslink oder Anfrage auf der Teamseite. */
function TeamCard({ me, onCreateTeam }: { me: ChallengeMe; onCreateTeam?: () => void }) {
  if (me.team) {
    const open = me.openRequestsToMyTeam ?? 0;
    return (
      <Link className="wc-card wc-teamcard" to={`/winterchallenge/team/${me.team.slug}`}>
        <Users size={20} aria-hidden />
        <span className="wc-teamcard-main">
          <span className="wc-kicker">Dein Team</span>
          <strong>{me.team.name}</strong>
          {open > 0 && <span className="wc-teamcard-note">{TEAM_REQUEST_TEXTS.openToMyTeam(open)}</span>}
        </span>
        <ArrowRight size={18} aria-hidden />
      </Link>
    );
  }
  const requests = me.teamRequests ?? [];
  const waiting = requests.filter(r => r.status === 'PENDING').length;
  return (
    <section className="wc-card">
      <span className="wc-kicker">Team</span>
      <h2 className="wc-h2" style={{ marginBottom: 6 }}>Zusammen sammeln</h2>
      <p className="wc-muted" style={{ marginTop: 0 }}>
        Mit Freunden, Kollegen oder deinem Verein. Einem Team trittst du über den Einladungslink bei. Ohne Link fragst du auf der Teamseite an, die Teams findest du in der Rangliste.
      </p>
      {requests.length > 0 && (
        <div className="wc-requests-mine">
          {waiting > 0 && <span className="wc-requests-mine-title"><Clock size={14} aria-hidden />{TEAM_REQUEST_TEXTS.waiting(waiting)}</span>}
          <ul>
            {requests.map(r => (
              <li key={r.team.slug}>
                <Link to={`/winterchallenge/team/${r.team.slug}`}>{r.team.name}</Link>
                <span className={`wc-chip${r.status === 'DECLINED' ? ' wc-chip--muted' : ''}`}>{r.status === 'PENDING' ? 'wartet' : 'abgelehnt'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {onCreateTeam && (
        // Gefüllt, nicht als Umriss: neben der Rangliste ging der Knopf unter (Phil, 25.10.2026).
        <button type="button" className="wc-btn wc-btn--block" onClick={onCreateTeam}>
          <Flag size={16} aria-hidden />Team gründen
        </button>
      )}
    </section>
  );
}
